import './request-card.scss';

import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import CarPlaceholder from '@/media/icons/car-placeholder.svg?react';
import ChevronDownIcon from '@/media/icons/chevron-down.svg?react';
import {
  REQUEST_KIND_LABELS,
  REQUEST_STATUS_META
} from '@/pages/Requests/constants/requests';
import type { IRequest } from '@/pages/Requests/types/request.types';
import { formatPrice } from '@/shared/services/formatters';

interface IProps {
  request: IRequest;
  isArchived?: boolean;
  onOpen?: (id: string) => void;
}

const RequestCard: React.FC<IProps> = ({ request, isArchived, onOpen }) => {
  const { t } = useTranslation();
  const {
    id,
    number,
    kind,
    status,
    statusLabel,
    vehicleName,
    vehicleDetails,
    imageUrl,
    vehiclePrice,
    downPayment,
    termMonths,
    vehicleTypeLabel,
    createDate
  } = request;

  const { tone } = REQUEST_STATUS_META[status];
  const formattedCreateDate = createDate
    ? dayjs(createDate).format('DD.MM.YYYY')
    : '';
  const isInteractive = !isArchived && Boolean(onOpen);

  const handleOpen = () => {
    if (!isInteractive) {
      return;
    }

    onOpen?.(id);
  };

  return (
    <div
      className={`request-card${isArchived ? ' request-card--archived' : ''}`}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={isInteractive ? handleOpen : undefined}
      onKeyDown={
        isInteractive
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleOpen();
              }
            }
          : undefined
      }
    >
      <div className="request-card__head-mobile">
        <span className="request-card__number">
          {t(REQUEST_KIND_LABELS[kind])} · {number}
        </span>
        <span className={`request-card__status request-card__status--${tone}`}>
          <span className="request-card__status-dot" />
          {t(statusLabel)}
        </span>
      </div>

      <div className="request-card__content">
        <div className="request-card__media">
          {imageUrl ? (
            <img
              className="request-card__image"
              src={imageUrl}
              alt={vehicleName}
              loading="lazy"
            />
          ) : (
            <span className="request-card__placeholder">
              <CarPlaceholder />
            </span>
          )}
        </div>

        <div className="request-card__main">
          <div className="request-card__head">
            <span className="request-card__number">
              {t(REQUEST_KIND_LABELS[kind])} · {number}
            </span>
            <span
              className={`request-card__status request-card__status--${tone}`}
            >
              <span className="request-card__status-dot" />
              {t(statusLabel)}
            </span>
          </div>
          <h3 className="request-card__vehicle">{vehicleName}</h3>
          <p className="request-card__details">{vehicleDetails}</p>
          {formattedCreateDate && (
            <time className="request-card__created" dateTime={createDate}>
              {t('requests.card.createdAt', { date: formattedCreateDate })}
            </time>
          )}
          <div className="request-card__payment-mobile">
            <span className="request-card__payment-value">
              {formatPrice(vehiclePrice)} ₸
            </span>
          </div>
        </div>

        {!isArchived && (
          <span className="request-card__chevron" aria-hidden="true">
            <ChevronDownIcon className="request-card__chevron-icon" />
          </span>
        )}
      </div>

      <div className="request-card__aside">
        <div className="request-card__metric">
          <span className="request-card__metric-label">
            {t('requests.card.price')}
          </span>
          <span className="request-card__metric-value">
            {formatPrice(vehiclePrice)} ₸
          </span>
        </div>
        <div className="request-card__metric">
          <span className="request-card__metric-label">
            {t('requests.card.downPayment')}
          </span>
          <span className="request-card__metric-value">
            {formatPrice(downPayment)} ₸
          </span>
        </div>
        <div className="request-card__metric">
          <span className="request-card__metric-label">
            {t('requests.card.term')}
          </span>
          <span className="request-card__metric-value">
            {t('common.monthsShort', { count: termMonths })}
          </span>
        </div>
        <div className="request-card__metric">
          <span className="request-card__metric-label">
            {t('requests.card.type')}
          </span>
          <span className="request-card__metric-value">
            {t(vehicleTypeLabel)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default RequestCard;
